import { CompanyBrandSettings } from "./CompanyBrandSettings";
import SettingsPage from "../components/SettingsPane/SettingsPage";
import { CheckboxFieldSettings } from "../SettingsScreen";

import { itx } from "../translationItx/itx";
import { ApplicationMode, enumAvailableLanguages } from "../models/SettingsEnums";
import { isUiModeDeveloper, uiModeWatcher } from "../uiMode";
import { useOnChangeWatcher } from "../models/useOnChangeWatcher";
import GroupAddress from "../models/GroupAdress";
import { hideToast, showToast, updateToastMessage, updateToastProgress } from "../components/Toasts";

// yield to let React render the toast progress update before continuing the loop
const yieldToUI = () => new Promise<void>((r) => setTimeout(r, 0));

export default function SettingPageGeneral(props: {
  handler: string;
  dropDownColor: string;
  setRefresh?: (number: number) => void;
  refresh?: number;
}) {
  const { dropDownColor, setRefresh, refresh } = props;
  useOnChangeWatcher(uiModeWatcher);
  useOnChangeWatcher(state.projectMetadata, property => property === "isC1Project");
  // const options = new Array<{ value: string; label: string }>();
  const languageOptions = new Array<any>();
  state.availiableLanguages.forEach((language) => {
    // options.push(languageOption(language));
    const { value, label } = languageOption(language);
    languageOptions.push(<option key={value} value={value}>{label}</option>);
  });

  function languageOption(language: any) {
    switch (language) {
      case enumAvailableLanguages.german:
        return {
          value: "de-DE",
          label: itx("SettingsScreen.german", "Deutsch"),
        };
      case enumAvailableLanguages.french:
        return {
          value: "fr-FR",
          label: itx("SettingsScreen.french", "Französisch"),
        };
      case enumAvailableLanguages.italian:
        return {
          value: "it-IT",
          label: itx("SettingsScreen.italian", "italian"),
        };
      case enumAvailableLanguages.spanish:
        return {
          value: "es-ES",
          label: itx("SettingsScreen.spanish", "spanish"),
        };
      case enumAvailableLanguages.swedish:
        return {
          value: "sv-SE",
          label: itx("SettingsScreen.swedish", "swedish"),
        };
      case enumAvailableLanguages.english:
      default:
        return {
          value: "en-US",
          label: itx("SettingsScreen.english", "English"),
        };
    }
  }

  return (
    <SettingsPage
      handler="/settings/general" // no itx
    >
      <div className="form-flex">
        <div className="form-left">
          <fieldset className="form-group">
            <label
              className="form-label-bold"
              htmlFor="profileName" // no itx
              key={"profileName_label_languageForInterface"} // no itx
            >
              {itx("SettingsScreen.languageForInterface", `Sprache für Benutzeroberfläche:`)}{" "}
            </label>

            <select
              //name="uiLanguage" // no itx
              key={state.settings.uiLanguage}
              className="select form-dropdown input-standard"
              defaultValue={state.settings.language}
              onChange={(ev: any) => {
                const language = ev.target.value;
                if (language) {
                  state.settings.uiLanguage = language;
                  if (setRefresh && refresh) {
                    setRefresh(refresh + 1);
                  }
                }
              }}
            >
              {languageOptions}
            </select>
          </fieldset>
          <section className="settings-card">
            <h3 className="settings-card__header">Firma</h3>
            <div className="settings-card__body"><CompanyBrandSettings /></div>
          </section>
          {
            <CheckboxFieldSettings
              name="secondScreenSupport" // no itx
              label="Zwei Bildschirme unterstützen" // no itx
              setRefresh={setRefresh}
              refresh={refresh}
            />
          }
          <CheckboxFieldSettings
            name="floorsHaveIndividualAxes"
            label={itx("SettingsScreen.floorsHaveIndividualAxes", "Eigene Achsen je Etage (unabhängige Grundrisse)")}
            setRefresh={setRefresh}
            refresh={refresh}
          />
          <button
            className="button"
            onClick={() => {
              recalculateAllDeviceChannels();
            }}
          >
            {itx("SettingsScreen.recalculateDevices", "Alle Geräte und dessen Kanäle neu berechnen")}
          </button>
          {state.settings.applicationMode === ApplicationMode.Main && isUiModeDeveloper() && (
            <button
              id="markProjectAsC1"
              type="button"
              className="button"
              disabled={state.projectMetadata.isC1Project}
              onClick={() => {
                if (!isUiModeDeveloper() || state.projectMetadata.isC1Project) return;
                state.projectMetadata.beforeChange("isC1Project");
                state.projectMetadata.isC1Project = true;
                state.projectMetadata.changed("isC1Project");
              }}
            >
              {state.projectMetadata.isC1Project
                ? itx("SettingsScreen.projectMarkedAsC1", "Projekt ist als C1-Projekt markiert")
                : itx("SettingsScreen.markProjectAsC1", "Projekt als C1-Projekt markieren")}
            </button>
          )}
        </div>
        <div className="form-right"></div>
      </div>
    </SettingsPage>
  );
}

async function recalculateAllDeviceChannels() {
  await showToast({
    title: itx("SettingsScreen.recalculateDevices", "Alle Geräte und dessen Kanäle neu berechnen"),
    text: itx("csvUploadDialog.updateDevices", "Die Geräte und deren Kanäle werden aktualisiert..."),
    keepVisible: true,
    progress: true,
  });
  // yield so React renders the progress bar before we start updating it
  await yieldToUI();

  await state.loadKnxMasterData().then(async (knxMasterData) => {
    const allDevices = state.allDevices();
    const total = allDevices.length;
    let processed = 0;
    for (const device of allDevices) {
      processed++;
      updateToastProgress({ progress: Math.round((processed / Math.max(total, 1)) * 100) });
      updateToastMessage(
        itx("SettingsScreen.recalculatingDeviceProgress", "Gerät T1/T2: T3", {
          T1: String(processed),
          T2: String(total),
          T3: device.name || device.tag,
        }),
      );
      await yieldToUI();

      // recalculate device logic here
      device.removeUnusedChannels_WithoutKnxChannel_And_WithoutConnections();

      GroupAddress.updateAllBackpointers();

      device.localConsistencyCheck();

      device.channels.forEach((channel) => {
        channel.signals.forEach((signal) => {
          if (signal.parent != channel) {
            console.error("signal.parent != channel (async check)", signal, channel); // no itx
            signal.parent = channel; // repair inconsistency
          }
        });
      });

      GroupAddress.updateAllBackpointers();

      device.localConsistencyCheck();

      device.channels.forEach((channel) => {
        channel.signals.forEach((signal) => {
          if (signal.parent != channel) {
            console.error("signal.parent != channel (sync check)", signal, channel); // no itx
            signal.parent = channel; // repair inconsistency
          }
        });
      });
    }
    await hideToast();
  });
  // update consistency after recalculating all device channels
  // (errorInChild is invalidated + re-rendered up the tree automatically via invalidatParentErrorStatus)
  state.consistencyCheck();
}
